import type { SearchResponse } from 'instantsearch.js';
import { useCallback, useMemo } from 'react';
import { useInstantSearch, useRefinementList } from 'react-instantsearch';

import type { RefineListProps } from '@sorare/core/src/components/search/RefineList';
import { AtomList } from '@sorare/core/src/components/search/RefineList';
import { useFacet } from '@sorare/core/src/contexts/graphql-search';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useSearchClient } from '@sorare/core/src/hooks/search/useSearchClient';
import type { AvailableLanguage } from '@sorare/core/src/i18n/useTranslations';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import type { Filter, FilterWidget } from '@sorare/core/src/lib/filters';

import { RefineListTitle } from 'search/RefineListTitle';

type Props = {
  visible: boolean;
  filter: Filter & { i18nAttribute: (language: AvailableLanguage) => string };
  filterBy?: (item: any) => boolean;
  sortBy?: RefineListProps['sortBy'];
  limit?: number;
  showMore?: boolean;
  searchable?: boolean;
  renderBefore?: RefineListProps['renderBefore'];
};

const formatOption = (label: string) => label.split('|')[0];
const MAX_SHOW_MORE_LIMIT = 40;

const RefineI18nList = ({
  visible,
  filter,
  filterBy,
  sortBy = ['isRefined:desc', 'name:asc'],
  renderBefore,
  limit: limitProps,
  showMore,
  searchable,
  ...rest
}: Props) => {
  const track = useEvents();
  const { language } = useIntlContext();
  const i18nAttribute = filter.i18nAttribute(language);

  const { up: isTabletOrDesktop } = useScreenSize('tablet');

  const limit = (() => {
    if (limitProps) return limitProps;
    if (isTabletOrDesktop) return 10;
    return MAX_SHOW_MORE_LIMIT / 2;
  })();

  const showMoreLimit = MAX_SHOW_MORE_LIMIT;

  const {
    items,
    isFromSearch,
    searchForItems,
    refine: doRefine,
    hasExhaustiveItems,
    isShowingMore,
    toggleShowMore,
  } = useRefinementList({
    attribute: i18nAttribute,
    sortBy,
    limit: MAX_SHOW_MORE_LIMIT - 1,
    showMoreLimit,
    ...rest,
    showMore,
  });

  const searchClient = useSearchClient();
  const { results } = useInstantSearch();

  const actualItems = useMemo(() => {
    return items
      .map(item => {
        const [name, code] = item.label.split('|');

        // If the item is refined but has no results, it probably means we're visiting a URL coming
        // from a different language
        // TODO(su): remove once most URLs in the wild are using the new format
        if (item.isRefined && item.count === 0) {
          // cancel the refined item in the wrong language
          doRefine(item.value);

          // refine the item in the correct language
          const currentLanguageItem = items.find(
            i =>
              i.value !== item.value &&
              (i.value.endsWith(`|${code}`) || i.value.startsWith(`${name}|`))
          );
          if (currentLanguageItem) {
            // we found it within the available facets
            doRefine(currentLanguageItem.value);
          } else {
            // do an extra search to find the correct value
            searchClient
              .search([
                {
                  indexName: results.index,
                  type: 'default',
                  params: {
                    analytics: false,
                    distinct: false,
                    hitsPerPage: 0,
                    facets: [i18nAttribute],
                    filters: `(${filter.attribute}:"${name}" OR ${filter.attribute}:"${code}")`,
                  },
                },
              ])
              .then(({ results: res }) => {
                const v = (res[0] as SearchResponse<any>).facets![
                  i18nAttribute
                ];
                if (v && Object.keys(v).length === 1) {
                  doRefine(Object.keys(v)[0]);
                }
              });
          }
          return undefined;
        }

        return item;
      })
      .filter(item => (filterBy ? filterBy(item) : true))
      .filter(Boolean)
      .slice(0, limit);
  }, [
    doRefine,
    filter.attribute,
    filterBy,
    i18nAttribute,
    items,
    results.index,
    searchClient,
    limit,
  ]);

  const refine = useCallback(
    (value: string) => {
      track('Use Market Filter', {
        filterName: filter.attribute,
        filterValue: value.split('|')[1],
      });

      doRefine(value);
    },
    [doRefine, filter.attribute, track]
  );

  return (
    <AtomList
      {...rest}
      attribute={i18nAttribute}
      items={actualItems}
      formatOption={formatOption}
      refine={refine}
      renderBefore={renderBefore}
      searchForItems={searchForItems}
      isFromSearch={isFromSearch}
      searchable={typeof searchable === 'boolean' ? searchable : true}
      visible={visible}
      hasExhaustiveItems={showMore === false ? true : hasExhaustiveItems}
      isShowingMore={isShowingMore}
      toggleShowMore={toggleShowMore}
    />
  );
};

export const RefineBackendI18nList = ({
  visible,
  attribute,
  filterBy,
  renderBefore,
  limit: limitProps,
  showMore,
  searchable,
  ...rest
}: Omit<Props, 'filter'> & { attribute: string }) => {
  const { items, refine, search, toggle, isFromSearch } = useFacet(attribute);

  const { up: isTabletOrDesktop } = useScreenSize('tablet');

  const limit = (() => {
    if (limitProps) return limitProps;
    if (isTabletOrDesktop) return 10;
    return MAX_SHOW_MORE_LIMIT / 2;
  })();

  return (
    <AtomList
      {...rest}
      attribute={attribute}
      items={items.slice(0, limit)}
      formatOption={formatOption}
      refine={value => refine(toggle([value]))}
      renderBefore={renderBefore}
      searchForItems={search}
      isFromSearch={isFromSearch}
      searchable={typeof searchable === 'boolean' ? searchable : true}
      visible={visible}
    />
  );
};

const RefineI18nListTitle = ({ filter }: Pick<Props, 'filter'>) => {
  const { language } = useIntlContext();

  return (
    <RefineListTitle
      attribute={filter.i18nAttribute(language)}
      name={filter.title}
    />
  );
};

export const buildRefineI18nList = ({
  filter,
  hideTitle,
  ...rest
}: Omit<Props, 'visible'> & {
  hideTitle?: boolean;
}): FilterWidget => ({
  key: filter.attribute,
  type: 'list',
  attribute: filter.attribute,
  Component: ({ visible }) => (
    <RefineI18nList visible={visible} filter={filter} {...rest} />
  ),
  title: hideTitle ? null : <RefineI18nListTitle filter={filter} />,
  accordionOptions: { startsOpen: false },
});
